import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { onTransactionChange } from './useTransactions';

// ── Module-level TTL cache ────────────────────────────────────────────────────
// Evita refetch do dashboard em trocas rápidas de aba / navegação.
// Cada user_id tem uma entrada independente. TTL: 30 segundos.
const CACHE_TTL_MS = 30_000;

interface DashboardCacheEntry {
  ts: number;
  stats: DashboardStats;
  accounts: AccountSummary[];
  chartRawData: ChartRawPoint[];
  upcomingBills: UpcomingBill[];
  recentTransactions: RecentTx[];
}

const dashboardCache = new Map<string, DashboardCacheEntry>();

/** Invalida o cache de um usuário — chamado após qualquer mutação de transações */
export const invalidateDashboardCache = (userId: string) => {
  dashboardCache.delete(userId);
};

export interface DashboardStats {
  totalBalance: number;
  monthlyIncome: number;
  monthlyExpenses: number;
  pendingMonthlyExpenses: number;
  pendingMonthlyIncome: number;
  pendingBillsTotal: number;
  pendingBillsCount: number;
  activeGoalsCount: number;
}

export interface ChartRawPoint {
  type: string;
  amount: number;
  date: string;
}

export interface UpcomingBill {
  id: string;
  description: string;
  amount: number;
  dueDate: string;
  daysLeft: number;
  category: string;
}

export interface RecentTx {
  id: string;
  description: string;
  category: string;
  type: 'receita' | 'despesa';
  amount: number;
  date: string;
  account: string;
  status: string;
}

export interface AccountSummary {
  id: string;
  name: string;
  balance: number;
  color: string;
}

const EMPTY_STATS: DashboardStats = {
  totalBalance: 0,
  monthlyIncome: 0,
  monthlyExpenses: 0,
  pendingMonthlyExpenses: 0,
  pendingMonthlyIncome: 0,
  pendingBillsTotal: 0,
  pendingBillsCount: 0,
  activeGoalsCount: 0,
};

export const useDashboardData = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>(EMPTY_STATS);
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [chartRawData, setChartRawData] = useState<ChartRawPoint[]>([]);
  const [upcomingBills, setUpcomingBills] = useState<UpcomingBill[]>([]);
  const [recentTransactions, setRecentTransactions] = useState<RecentTx[]>([]);
  // Ref to track if this is a forced refresh (skip cache)
  const forceRefreshRef = useRef(false);

  const fetchAll = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    // ── Cache hit: se dados frescos existem, serve do cache sem spinner ────
    const cached = dashboardCache.get(user.id);
    if (cached && !forceRefreshRef.current && Date.now() - cached.ts < CACHE_TTL_MS) {
      setStats(cached.stats);
      setAccounts(cached.accounts);
      setChartRawData(cached.chartRawData);
      setUpcomingBills(cached.upcomingBills);
      setRecentTransactions(cached.recentTransactions);
      setLoading(false);
      return;
    }
    forceRefreshRef.current = false;

    setLoading(true);
    try {
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
        .toISOString().split('T')[0];
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        .toISOString().split('T')[0];
      const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
        .toISOString().split('T')[0];
      const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 11, 1)
        .toISOString().split('T')[0];

      const [
        accountsRes,
        monthlyTxRes,
        upcomingRes,
        recentRes,
        goalsRes,
        chartTxRes,
      ] = await Promise.all([
        supabase
          .from('accounts')
          .select('id, name, current_balance, color')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .order('created_at', { ascending: true }),

        // Fetch all monthly transactions with status so we can split realized vs pending
        // transaction_subtype needed to exclude transfers from income/expense stats
        supabase
          .from('transactions')
          .select('type, amount, status, transaction_subtype')
          .eq('user_id', user.id)
          .gte('date', monthStart)
          .lte('date', monthEnd)
          .is('deleted_at', null),

        supabase
          .from('transactions')
          .select('id, description, amount, date, category')
          .eq('user_id', user.id)
          .eq('type', 'despesa')
          .in('status', ['pendente', 'vencido', 'atrasado'])
          .gte('date', today)
          .lte('date', in30Days)
          .is('deleted_at', null)
          .order('date', { ascending: true })
          .limit(100),

        supabase
          .from('transactions')
          .select('id, description, category, type, amount, date, status, account_id')
          .eq('user_id', user.id)
          .is('deleted_at', null)
          .order('date', { ascending: false })
          .limit(20),

        supabase
          .from('goals')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('is_completed', false),

        supabase
          .from('transactions')
          .select('type, amount, date, status, transaction_subtype')
          .eq('user_id', user.id)
          .gte('date', twelveMonthsAgo)
          .lte('date', monthEnd)
          .is('deleted_at', null),
      ]);

      // ─── Accounts ─────────────────────────────────────────────────────────
      const accs: AccountSummary[] = (accountsRes.data ?? []).map((a: any) => ({
        id: a.id,
        name: a.name,
        balance: Number(a.current_balance ?? 0),
        color: a.color ?? '#10B981',
      }));
      setAccounts(accs);

      // ─── Monthly stats ─────────────────────────────────────────────────────
      // RULE: monthlyIncome/monthlyExpenses show REALIZED amounts only.
      // Only 'recebido' receitas and 'pago' despesas count as realized.
      // This matches the account balance rule in accountBalance.ts.
      const monthlyTxData = monthlyTxRes.data ?? [];
      // CRÍTICO: excluir transferências dos totais mensais — evita dupla contagem
      const monthlyIncome = monthlyTxData
        .filter((t: any) => t.type === 'receita' && t.status === 'recebido' && t.transaction_subtype !== 'transferencia')
        .reduce((s: number, t: any) => s + Number(t.amount), 0);
      const monthlyExpenses = monthlyTxData
        .filter((t: any) => t.type === 'despesa' && t.status === 'pago' && t.transaction_subtype !== 'transferencia')
        .reduce((s: number, t: any) => s + Number(t.amount), 0);
      const pendingMonthlyExpenses = monthlyTxData
        .filter((t: any) => t.type === 'despesa' && ['pendente', 'vencido', 'atrasado'].includes(t.status) && t.transaction_subtype !== 'transferencia')
        .reduce((s: number, t: any) => s + Number(t.amount), 0);
      const pendingMonthlyIncome = monthlyTxData
        .filter((t: any) => t.type === 'receita' && ['pendente', 'atrasado'].includes(t.status) && t.transaction_subtype !== 'transferencia')
        .reduce((s: number, t: any) => s + Number(t.amount), 0);

      // ─── Upcoming bills ────────────────────────────────────────────────────
      const todayDate = new Date(today + 'T00:00:00');
      const bills: UpcomingBill[] = (upcomingRes.data ?? []).map((b: any) => {
        const due = new Date(b.date + 'T00:00:00');
        const daysLeft = Math.ceil(
          (due.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24)
        );
        return {
          id: b.id,
          description: b.description ?? 'Conta',
          amount: Number(b.amount),
          dueDate: b.date,
          daysLeft,
          category: b.category ?? 'Outros',
        };
      });
      setUpcomingBills(bills);

      // ─── Recent transactions ───────────────────────────────────────────────
      const accNameMap: Record<string, string> = {};
      accs.forEach((a) => { accNameMap[a.id] = a.name; });

      const recent: RecentTx[] = (recentRes.data ?? [])
        // Excluir transações de contas arquivadas/excluídas (account_id não nulo
        // mas não presente nas contas ativas). Transações com account_id nulo
        // (cartões, sem conta vinculada) são mantidas normalmente.
        .filter((t: any) => !t.account_id || accNameMap[t.account_id] !== undefined)
        .map((t: any) => ({
          id: t.id,
          description: t.description ?? '',
          category: t.category ?? 'Outros',
          type: t.type as 'receita' | 'despesa',
          amount: Number(t.amount),
          date: t.date ?? '',
          account: t.account_id ? (accNameMap[t.account_id] ?? '—') : '—',
          status: t.status ?? 'pendente',
        }));
      setRecentTransactions(recent);

      // ─── Aggregate stats ───────────────────────────────────────────────────
      // totalBalance = sum of account current_balance values.
      // Each account's current_balance is maintained by syncAccountBalance
      // which ONLY counts pago/recebido — so this is always the real balance.
      const totalBalance = accs.reduce((s, a) => s + a.balance, 0);
      const pendingBillsTotal = bills.reduce((s, b) => s + b.amount, 0);

      setStats({
        totalBalance,
        monthlyIncome,
        monthlyExpenses,
        pendingMonthlyExpenses,
        pendingMonthlyIncome,
        pendingBillsTotal,
        pendingBillsCount: bills.length,
        activeGoalsCount: goalsRes.count ?? 0,
      });

      // ─── Chart raw data ────────────────────────────────────────────────────
      // CRÍTICO 1: excluir transferências — evita dupla contagem de receita/despesa.
      // CRÍTICO 2: incluir SOMENTE transações realizadas (pago/recebido) para que
      // o gráfico histórico seja consistente com os cards de stats do dashboard.
      // Pendentes e vencidos não aparecem no gráfico — igual ao critério dos cards.
      const chartData = (chartTxRes.data ?? [])
          .filter((t: any) => t.transaction_subtype !== 'transferencia')
          .filter((t: any) =>
            (t.type === 'receita' && t.status === 'recebido') ||
            (t.type === 'despesa' && t.status === 'pago')
          )
          .map((t: any) => ({
            type: t.type as string,
            amount: Number(t.amount),
            date: t.date ?? '',
          }));
      setChartRawData(chartData);

      // ── Armazena no cache ─────────────────────────────────────────────────
      if (user) {
        dashboardCache.set(user.id, {
          ts: Date.now(),
          stats: {
            totalBalance, monthlyIncome, monthlyExpenses,
            pendingMonthlyExpenses, pendingMonthlyIncome,
            pendingBillsTotal, pendingBillsCount: bills.length,
            activeGoalsCount: goalsRes.count ?? 0,
          },
          accounts: accs,
          chartRawData: chartData,
          upcomingBills: bills,
          recentTransactions: recent,
        });
      }
    } catch (err) {
      console.error('[useDashboardData]', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Listen for transaction changes from any part of the app ───────────────
  // When useTransactions creates/updates/deletes a transaction, it broadcasts
  // 'transactions:changed'. We invalidate the cache and refetch immediately.
  useEffect(() => {
    return onTransactionChange(() => {
      if (user) invalidateDashboardCache(user.id);
      forceRefreshRef.current = true;
      fetchAll();
    });
  }, [fetchAll, user]);

  return {
    loading,
    stats,
    accounts,
    chartRawData,
    upcomingBills,
    recentTransactions,
    refetch: fetchAll,
  };
};

/** Build chart data points for the last `months` months from raw transaction list */
export const buildChartData = (
  rawData: ChartRawPoint[],
  months: number
): { mes: string; receitas: number; despesas: number }[] => {
  const now = new Date();
  return Array.from({ length: months }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (months - 1 - i), 1);
    const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('pt-BR', { month: 'short' });
    const mes = label.charAt(0).toUpperCase() + label.slice(1).replace('.', '');

    const monthTx = rawData.filter((t) => t.date?.startsWith(monthKey));
    const receitas = monthTx
      .filter((t) => t.type === 'receita')
      .reduce((s, t) => s + t.amount, 0);
    const despesas = monthTx
      .filter((t) => t.type === 'despesa')
      .reduce((s, t) => s + t.amount, 0);

    return { mes, receitas, despesas };
  });
};
